<template>
  <div class="table-responsive mb-3 worker-table-wrap">
    <table class="table table-sm mb-0 worker-table">
      <thead>
        <tr>
          <th class="worker-heading">Worker</th>
          <th
            v-for="day in weekDayHeaders"
            :key="day.label"
            class="day-heading text-center small"
          >
            <span class="day-heading-label">{{ day.label }}</span>
            <span class="day-heading-date">{{ day.date }}</span>
          </th>
          <th class="text-center total-heading">Total</th>
          <th class="text-center on-site-heading">On Site</th>
        </tr>
      </thead>
      <tbody v-if="editing">
        <tr v-for="(row, ri) in editRows" :key="row.worker.id">
          <td class="align-middle worker-name-cell">
            <div class="worker-identity">
              <b-form-checkbox
                :checked="row.on_site"
                class="worker-on-site-toggle"
                @change="$emit('toggle-worker', ri, $event)"
              >
                <span class="worker-name">{{ row.worker.full_name }}</span>
              </b-form-checkbox>
              <div v-if="row.worker.trade" class="worker-trade">
                {{ formatTrade(row.worker.trade) }}
              </div>
            </div>
          </td>
          <td
            v-for="(day, di) in row.days"
            :key="day.date"
            class="day-cell"
            :class="{
              'day-cell-muted': !row.on_site,
              'day-cell-active': day.present,
              'day-cell-leave': isLeaveDay(day),
            }"
          >
            <template v-if="row.on_site">
              <b-form-select
                :value="day.leave_type"
                :options="leaveTypeOptions"
                size="sm"
                class="leave-type-select"
                @change="$emit('set-leave-type', ri, di, $event)"
              />
              <template v-if="!isLeaveDay(day)">
                <button
                  v-if="!day.present"
                  type="button"
                  class="day-add-btn"
                  :aria-label="`Add ${row.worker.full_name} day ${di + 1}`"
                  @click="$emit('set-present', ri, di, true)"
                >
                  <b-icon-plus />
                  <span>Add day</span>
                </button>

                <div v-else class="day-edit-card">
                  <div
                    class="day-edit-card-header day-edit-card-header--actions"
                  >
                    <button
                      type="button"
                      class="day-remove-btn"
                      :aria-label="`Remove ${row.worker.full_name} day ${
                        di + 1
                      }`"
                      @click="$emit('set-present', ri, di, false)"
                    >
                      <b-icon-x />
                    </button>
                  </div>

                  <div class="day-fields">
                    <label class="day-field">
                      <span>Start</span>
                      <datetime-input
                        :value="day.start"
                        type="time"
                        size="sm"
                        @input="$emit('set-day-field', ri, di, 'start', $event)"
                      />
                    </label>
                    <label class="day-field">
                      <span>Finish</span>
                      <datetime-input
                        :value="day.finish"
                        type="time"
                        size="sm"
                        @input="
                          $emit('set-day-field', ri, di, 'finish', $event)
                        "
                      />
                    </label>
                    <label class="day-field">
                      <span>Break</span>
                      <b-form-input
                        :value="day.break_minutes"
                        type="number"
                        min="0"
                        step="5"
                        size="sm"
                        placeholder="Mins"
                        @input="
                          $emit(
                            'set-day-field',
                            ri,
                            di,
                            'break_minutes',
                            Number($event),
                          )
                        "
                      />
                    </label>
                    <div v-if="dayHoursLabel(day)" class="day-hours">
                      {{ dayHoursLabel(day) }}
                      <span v-if="day.break_minutes">
                        · {{ day.break_minutes }}m break
                      </span>
                    </div>
                    <div v-if="isNightDay(day)" class="day-night">
                      <span class="day-night-badge">Night</span>
                    </div>
                    <div v-if="isOvernightDay(day)" class="day-overnight">
                      Overnight - ends next morning
                    </div>
                    <div v-if="dayError(day)" class="day-error">
                      {{ dayError(day) }}
                    </div>
                    <div
                      v-if="hasOtherProjectConflict(day)"
                      class="day-conflict"
                      :title="day.other_project_name"
                    >
                      Also clocked in at {{ day.other_project_name }}
                    </div>
                    <div
                      v-if="hasSupervisorHoursWarning(day)"
                      class="day-conflict"
                    >
                      Already clocked in at
                      {{
                        formatBusinessTime(
                          day.active_clock_in_at,
                          undefined,
                          sheet.timezone,
                        )
                      }}
                    </div>
                  </div>
                </div>
              </template>
              <div v-else class="day-edit-card leave-day-card">
                <span class="leave-day-label">{{
                  leaveTypeLabel(day.leave_type)
                }}</span>
                <label class="day-field leave-hours-field">
                  <span>Leave hours</span>
                  <b-form-input
                    :value="day.leave_hours"
                    type="number"
                    min="0"
                    max="40"
                    step="0.1"
                    size="sm"
                    placeholder="Hours"
                    @input="$emit('set-leave-hours', ri, di, $event)"
                  />
                </label>
                <div v-if="leaveHoursErrorFor(day)" class="day-error">
                  {{ leaveHoursErrorFor(day) }}
                </div>
              </div>
            </template>
            <span v-else class="offsite-mark">-</span>
            <!--
              Read-only in edit mode, deliberately: editRows is a detached
              deep clone (see startEdit), while the tab's amend handlers walk
              the live sheets tree. An amend control here would write to one
              object and render the other, so the change would look like it
              never happened. Display only.
            -->
            <div
              v-if="day && day.plant_entries && day.plant_entries.length"
              class="readonly-plant-list"
            >
              <div
                v-for="pe in day.plant_entries"
                :key="pe.id"
                class="readonly-plant-line"
              >
                {{ pe.plant_name }} · {{ pe.quantity }} × ${{
                  formatCurrency(pe.rate_snapshot)
                }}{{ pe.rate_type_snapshot === 'hourly' ? '/hr' : '/day' }} =
                ${{ formatCurrency(pe.claim_value) }}
              </div>
            </div>
          </td>
          <td
            class="text-center align-middle font-weight-bold small total-cell"
          >
            <span class="total-hours-badge">{{ rowTotal(row) }}</span>
          </td>
          <td class="text-center align-middle on-site-cell">
            <b-badge
              pill
              :variant="zoneStatusVariant(rowZoneStatus(row))"
              class="on-site-badge"
            >
              {{ zoneStatusLabel(rowZoneStatus(row)) }}
            </b-badge>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr v-for="row in viewRows" :key="row.worker.id">
          <td class="align-middle worker-name-cell">
            <div class="worker-identity">
              <div class="worker-name">
                {{ row.worker.full_name }}
              </div>
              <div v-if="row.worker.trade" class="worker-trade">
                {{ formatTrade(row.worker.trade) }}
              </div>
            </div>
          </td>
          <td
            v-for="(day, i) in row.days"
            :key="i"
            class="readonly-day-cell text-center align-middle small"
            :class="{
              'readonly-day-cell-empty': !day.present && !isLeaveDay(day),
              'readonly-day-cell-leave': isLeaveDay(day),
            }"
          >
            <template v-if="day.present && day.start && day.finish">
              <div
                class="readonly-day-card"
                :class="{ 'readonly-day-card--night': isNightDay(day) }"
              >
                <span class="readonly-day-time">
                  {{ day.start }} – {{ day.finish }}
                </span>
                <span v-if="isNightDay(day)" class="day-night-badge">
                  Night
                </span>
                <span class="readonly-day-summary">
                  <span v-if="dayHoursLabel(day)">{{
                    dayHoursLabel(day)
                  }}</span>
                  <span
                    v-if="dayHoursLabel(day) && day.break_minutes"
                    aria-hidden="true"
                  >
                    ·
                  </span>
                  <span v-if="day.break_minutes">
                    {{ day.break_minutes }}m break
                  </span>
                </span>
                <div
                  v-if="hasOtherProjectConflict(day)"
                  class="day-conflict day-conflict-readonly"
                  :title="day.other_project_name"
                >
                  Also clocked in at {{ day.other_project_name }}
                </div>
                <div
                  v-if="hasSupervisorHoursWarning(day)"
                  class="day-conflict day-conflict-readonly"
                >
                  Already clocked in at
                  {{
                    formatBusinessTime(
                      day.active_clock_in_at,
                      undefined,
                      sheet.timezone,
                    )
                  }}
                </div>
              </div>
            </template>
            <template v-else-if="isLeaveDay(day)">
              <div class="readonly-leave-card">
                <span class="readonly-leave-label">{{
                  leaveTypeLabel(day.leave_type)
                }}</span>
                <span v-if="leaveHoursLabel(day)" class="readonly-leave-hours">
                  {{ leaveHoursLabel(day) }}
                </span>
              </div>
            </template>
            <template v-else>
              <span class="empty-day-pill">Not added</span>
            </template>
            <div
              v-if="day.plant_entries && day.plant_entries.length"
              class="readonly-plant-list"
            >
              <div
                v-for="pe in day.plant_entries"
                :key="pe.id"
                class="readonly-plant-line"
              >
                <template v-if="editingPlantEntryId === pe.id">
                  <b-form-input
                    :value="plantEntryEditQuantity"
                    type="number"
                    step="0.01"
                    min="0"
                    size="sm"
                    class="plant-entry-qty-input"
                    @input="
                      $emit('update:plantEntryEditQuantity', Number($event))
                    "
                    @keyup.enter="$emit('save-plant-entry-edit', pe)"
                    @keyup.esc="$emit('cancel-plant-entry-edit')"
                  />
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0 ml-1"
                    :disabled="plantEntrySaving"
                    @click="$emit('save-plant-entry-edit', pe)"
                  >
                    <b-icon-check />
                  </b-button>
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0 text-muted"
                    @click="$emit('cancel-plant-entry-edit')"
                  >
                    <b-icon-x />
                  </b-button>
                </template>
                <template v-else>
                  {{ pe.plant_name }} · {{ pe.quantity }} × ${{
                    formatCurrency(pe.rate_snapshot)
                  }}{{ pe.rate_type_snapshot === 'hourly' ? '/hr' : '/day' }} =
                  ${{ formatCurrency(pe.claim_value) }}
                  <b-button
                    v-if="canManagePlant"
                    variant="link"
                    size="sm"
                    class="p-0 ml-1 plant-entry-edit-btn"
                    @click="$emit('start-plant-entry-edit', pe)"
                  >
                    <b-icon-pencil font-scale="0.8" />
                  </b-button>
                  <b-badge
                    v-if="pe.is_amended"
                    variant="warning"
                    pill
                    class="ml-1 plant-amended-badge"
                    style="cursor: pointer"
                    @click="$emit('open-amendment-history', pe)"
                  >
                    Amended ×{{ pe.amendment_count }}
                  </b-badge>
                </template>
              </div>
            </div>
          </td>
          <td
            class="text-center align-middle font-weight-bold small total-cell"
          >
            <span class="total-hours-badge">{{ rowTotal(row) }}</span>
          </td>
          <td class="text-center align-middle on-site-cell">
            <b-badge
              pill
              :variant="zoneStatusVariant(rowZoneStatus(row))"
              class="on-site-badge"
            >
              {{ zoneStatusLabel(rowZoneStatus(row)) }}
            </b-badge>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  formatTrade,
  leaveTypeLabel,
  isLeaveDay,
  dayMinutes,
  formatCurrency,
  formatHours,
  isOvernight,
  isNightStart,
  dayError as sharedDayError,
  dayLeaveHours,
  leaveHoursError,
  formatBusinessTime,
} from '@/utils/timesheetHelpers'
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'

export default {
  name: 'SupervisorWorkerTable',

  components: { DatetimeInput },

  props: {
    sheet: { type: Object, required: true },
    editing: { type: Boolean, default: false },
    editRows: { type: Array, default: null },
    viewRows: { type: Array, required: true },
    weekDayHeaders: { type: Array, required: true },
    leaveTypeOptions: { type: Array, required: true },
    canManagePlant: { type: Boolean, default: false },
    editingPlantEntryId: { type: Number, default: null },
    plantEntryEditQuantity: { type: Number, default: null },
    plantEntrySaving: { type: Boolean, default: false },
    rowZoneStatus: { type: Function, required: true },
    zoneStatusVariant: { type: Function, required: true },
    zoneStatusLabel: { type: Function, required: true },
  },

  methods: {
    formatTrade,
    leaveTypeLabel,
    isLeaveDay,
    formatCurrency,
    formatBusinessTime,

    dayHoursLabel(day) {
      return formatHours(dayMinutes(day)) || null
    },

    isOvernightDay(day) {
      return !!day && day.present && isOvernight(day.start, day.finish)
    },
    isNightDay(day) {
      return (
        !!day &&
        day.present &&
        !isLeaveDay(day) &&
        isNightStart(day.start, day.finish)
      )
    },

    dayError(day) {
      return sharedDayError(day)
    },

    // Informational only — a worker can legitimately be booked on more than
    // one project the same day, so this never blocks save/submit.
    hasOtherProjectConflict(day) {
      return !!day && day.present && !!day.other_project_conflict
    },

    // Reverse of hasOtherProjectConflict — the tradie has an open mobile
    // clock-in for this day that this supervisor/BA entry didn't come from.
    // Informational only, never blocks save/submit.
    hasSupervisorHoursWarning(day) {
      return !!day && day.present && !!day.active_clock_in_conflict
    },

    leaveHoursLabel(day) {
      const h = dayLeaveHours(day)
      return h != null ? `${h}h` : null
    },

    leaveHoursErrorFor(day) {
      return leaveHoursError(day)
    },

    rowTotal(row) {
      const mins = row.days.reduce((sum, day) => sum + dayMinutes(day), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';
@import '~@/assets/scss/layouts/supervisor-worker-shared';

.worker-table-wrap {
  margin-bottom: 1rem !important;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  background: #ffffff;
}

@media (max-width: 1199.98px) {
  .worker-table-wrap {
    display: none;
  }
}

.worker-table {
  width: 100%;
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.worker-name-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: 200px;
  max-width: 250px;
  background: #ffffff;
  box-shadow: 8px 0 18px rgba(15, 23, 42, 0.04);
}

.worker-table thead th.worker-heading {
  position: sticky;
  left: 0;
  z-index: 3;
  min-width: 200px;
  background-color: #f8fafc;
  box-shadow: 8px 0 18px rgba(15, 23, 42, 0.04);
}

// Not position:sticky. A right-anchored sticky column docks at the wrap's
// visible edge the instant the table needs horizontal scroll (see
// worker-name-cell's left-sticky for contrast: it only pins once you scroll
// past it). Because that dock offset comes from the wrap's width, not from
// any day-column boundary, it lands wherever it lands relative to the
// columns underneath — usually mid-column, not after the last one. With 7
// day columns that's the common case, not an edge case, so Total/On Site
// visibly split whichever day column their fixed offset happens to fall on.
// Letting them scroll like every other column removes the bug entirely.
.total-cell,
.worker-table td.on-site-cell {
  background: #ffffff;
}

.total-cell {
  width: 92px;
}

.worker-table td.on-site-cell {
  width: 82px;
}

.worker-table thead th.total-heading,
.worker-table thead th.on-site-heading {
  background-color: #f3f4f6;
}

.worker-table thead th.total-heading {
  width: 92px;
}

.worker-table thead th.on-site-heading {
  width: 82px;
}

.worker-table tbody tr:nth-child(even) td.worker-name-cell,
.worker-table tbody tr:nth-child(even) td.total-cell,
.worker-table tbody tr:nth-child(even) td.on-site-cell {
  background: #fcfcfd;
}

.worker-table tbody tr:hover td.worker-name-cell,
.worker-table tbody tr:hover td.total-cell,
.worker-table tbody tr:hover td.on-site-cell {
  background: #f8fafc;
}

.worker-table thead th {
  padding: 0.9rem 0.8rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  border-top: none;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.day-heading {
  min-width: 96px;
}

.day-heading-label,
.day-heading-date {
  display: block;
  line-height: 1.25;
}

.day-heading-label {
  color: #374151;
  font-size: 0.78rem;
}

.day-heading-date {
  margin-top: 0.15rem;
  color: #9ca3af;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.worker-table td,
.worker-table th {
  border-top: none;
  border-bottom: 1px solid #eef2f7;
  font-size: 0.8rem;
  vertical-align: middle;
}

.worker-table th {
  white-space: nowrap;
}

.worker-table td {
  padding: 0.9rem 0.8rem;
}

.worker-name-cell {
  padding: 1.15rem 1rem;
}

.worker-identity {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.worker-on-site-toggle {
  margin-bottom: 0;
}

.worker-name {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.25;
}

.worker-trade {
  margin-left: 1.55rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.35;
}

.worker-table tbody tr:last-child td {
  border-bottom: none;
}

.worker-table tbody tr:nth-child(even) td {
  background: #fcfcfd;
}

.worker-table tbody tr:hover td {
  background: #f8fafc;
}

.total-cell {
  color: #006cff;
}

.day-cell {
  min-width: 162px;
  padding: 0.85rem;
  background: #ffffff;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.day-cell-active {
  background: #ffffff;
  box-shadow: none;
}

.day-cell-muted {
  background: #f1f5f9;
  color: #94a3b8;
}

.day-cell-leave {
  background: #ffffff;
  box-shadow: none;
}

.day-edit-card.leave-day-card {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border-color: #fed7aa;
  background: #fff7ed;
}

.leave-day-label,
.readonly-leave-label {
  color: $leave-text;
  font-weight: 700;
  font-size: 0.78rem;
}

.readonly-leave-card {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.18rem;
  min-width: 96px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  background: #fff7ed;
  color: $leave-text;
}

.readonly-leave-card .readonly-leave-label {
  font-size: 0.62rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.readonly-leave-hours {
  color: $leave-text;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.2;
}

// Matches the blue "in-progress" treatment SupervisorWorkerCards already
// uses for its active mobile edit row (.mobile-day-row-edit.mobile-day-row-
// active) — reusing it here instead of the old flat green keeps one visual
// language for "this is being edited" across the desktop table and mobile
// cards, and reads as current rather than a leftover early-days color.
.day-edit-card {
  position: relative;
  padding: 0.65rem;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 72%);
}

.day-edit-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.45rem;
  margin-bottom: 0.55rem;
}

.day-edit-card-header--actions {
  justify-content: flex-end;
  margin-bottom: 0.3rem;
}

.day-fields {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

// Stacked (label above input), not a side-by-side grid — a fixed 42px label
// column left only minmax(82px, 1fr) for the input, which doesn't grow to
// fit DatetimeInput's 148px min-width floor for type="time" (explicit-length
// grid minimums don't expand for oversized content — see DatetimeInput's own
// comment). Confirmed by measurement: the input overflowed this day-cell by
// 31px into the next column. Stacking gives the input the day-cell's full
// width instead of splitting it with the label.
.day-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0;
}

.day-field > span {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.day-fields /deep/ .datetime-input,
.day-fields /deep/ .form-control {
  height: 32px;
  min-height: 32px;
  border-color: #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 600;
}

// Icon is decorative (the field opens its picker on click anywhere) — drop
// it here too, same as SupervisorWorkerCards, so the reclaimed width goes to
// the time text instead.
.day-fields /deep/ input[type='time'].datetime-input {
  padding-right: 10px;
}

.day-fields /deep/ .datetime-icon {
  display: none;
}

.day-hours {
  margin-top: 0.1rem;
  padding: 0.35rem 0.45rem;
  border-radius: 8px;
  background: #f8fafc;
  color: #166534;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.25;
}

.offsite-mark {
  display: block;
  padding: 1.1rem 0.35rem;
  color: #94a3b8;
  font-size: 1rem;
  text-align: center;
}

.readonly-day-cell {
  min-width: 96px;
}

.readonly-day-card {
  display: inline-flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: 0.2rem;
  min-width: 96px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #d1f0dc;
  border-radius: 10px;
  background: #f4fbf7;
  color: #166534;
}

.readonly-day-time {
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.day-night {
  margin-top: 0.15rem;
}

.day-night-badge {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.62rem;
  font-weight: 600;
}

.readonly-day-card--night {
  border-color: #c7d2fe;
  background: #eef2ff;
  color: #3730a3;
}

.readonly-day-summary {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  color: #5f6f65;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}
</style>
